import { ServiceSelectionAction } from 'actions/SelectedServicesActions';

import { TProfessional } from 'types/appointment';

import {
  CLEAR_SERVICES,
  MAX_APPOINTMENTS_WARNING_ACKNOWLEDGED,
  SELECT_SERVICE,
  UNSELECT_SERVICE,
} from '../actions/ActionTypes';
import {
  flattenAppointments,
  optimizeAppointments,
  unflattenAppointments,
} from './bookableAppointmentUtils';

type Action = ServiceSelectionAction;

export type BookableAppointmentState = {
  serviceProviders: TProfessional[];
  appointments: BookableAppointment[];
  showMultiAppointmentWarning: boolean;
  maxAppointmentsWarning: {
    show: boolean;
    triggeringServiceGuid: string;
  };
};

export type BookableService = {
  guid: string;
  bookingOrder?: number;
};

export type BookableAppointment = {
  providerGuid?: string;
  services: BookableService[];
  auxProviderGuid?: string;
};

export const initialState: BookableAppointmentState = {
  serviceProviders: [],
  appointments: [],
  showMultiAppointmentWarning: false,
  maxAppointmentsWarning: {
    show: false,
    triggeringServiceGuid: '',
  },
};

const MAX_APPOINTMENTS = 3;

/**
 * Adds a service to the appointments array. If there is an appointment that can fulfill the service, add it to that appointment.
 * If not, create a new appointment.
 *
 * @param state the current state of the store
 * @param serviceGuid the service being added
 * @param providerGuid the selected provider
 * @returns BookableAppointmentState
 */
function addService(
  state: BookableAppointmentState,
  serviceGuid: string,
  providerGuid?: string,
  bookingOrder?: number,
  optionId?: string,
): BookableAppointmentState {
  const { appointments } = state;
  const isBookingOrderNumber = typeof bookingOrder === 'number';
  const newBookingOrder = isBookingOrderNumber ? bookingOrder : -1;

  const flattenedAppointments = flattenAppointments([
    ...appointments,
    {
      providerGuid,
      services: [
        {
          guid: optionId || serviceGuid,
          bookingOrder: newBookingOrder,
        },
      ],
    },
  ]);
  const sortedAppointments = flattenedAppointments.sort((a, b) => {
    return Number(a.bookingOrder) - Number(b.bookingOrder);
  });
  const unflattenedAppointments = unflattenAppointments(sortedAppointments);
  const optimizedAppointments = optimizeAppointments(
    unflattenedAppointments,
    state,
  );

  if (optimizedAppointments.length > MAX_APPOINTMENTS) {
    return {
      ...state,
      maxAppointmentsWarning: {
        show: true,
        triggeringServiceGuid: optionId || serviceGuid,
      },
    };
  }

  return {
    ...state,
    appointments: optimizedAppointments,
  };
}

/**
 *
 * @param state the current state of the store
 * @param serviceGuid the service being removed
 * @returns
 */
function removeService(
  state: BookableAppointmentState,
  serviceGuid: string,
): BookableAppointmentState {
  const { appointments } = state;
  const appointmentIndex = appointments.findIndex((appointment) =>
    appointment.services.some(({ guid }) => guid === serviceGuid),
  );

  if (appointmentIndex !== -1) {
    // If an appointment exists for this provider, remove the service
    const existingAppointment = appointments[appointmentIndex];
    const updatedServices = existingAppointment.services.filter(
      ({ guid }) => guid !== serviceGuid,
    );

    if (updatedServices.length === 0) {
      // If no services are left for this appointment, remove it
      return {
        ...state,
        appointments: [
          ...appointments.slice(0, appointmentIndex),
          ...appointments.slice(appointmentIndex + 1),
        ],
      };
    } else {
      // If services are left for this appointment, update it
      const updatedAppointment = {
        ...existingAppointment,
        services: updatedServices,
      };

      // TODO: Do we re-balance appointments here? There are cases where removing a service from an appointment
      // leads to a situation where another appointment could be deleted and it's services merged into another appointment
      // in the array

      return {
        ...state,
        appointments: [
          ...appointments.slice(0, appointmentIndex),
          updatedAppointment,
          ...appointments.slice(appointmentIndex + 1),
        ],
      };
    }
  }
  return state;
}

// eslint-disable-next-line import/no-default-export -- Redux convention
export default function bookableAppointments(
  state = initialState,
  action: Action,
): BookableAppointmentState {
  switch (action.type) {
    case CLEAR_SERVICES:
      return {
        ...state,
        appointments: [],
      };
    case SELECT_SERVICE: {
      const { providerGuid, guid, optionId, bookingOrder } = action.payload;

      return {
        ...addService(state, guid, providerGuid, bookingOrder, optionId),
      };
    }
    case UNSELECT_SERVICE: {
      const { guid, optionId } = action.payload;

      return {
        ...removeService(state, optionId || guid),
      };
    }
    case MAX_APPOINTMENTS_WARNING_ACKNOWLEDGED:
      return {
        ...state,
        maxAppointmentsWarning: {
          show: false,
          triggeringServiceGuid: '',
        },
      };
    default:
      return state;
  }
}
